/**
 * @description This service is used to determine if a user is eligible to fulfill an item.
 * It is primarily used with Search Results and those components that display search results.
 */
import { Injectable, Pipe, PipeTransform } from '@angular/core';
import { Document, Result, DocFormat } from '../core/interfaces/search.interface';
import { HoldService } from '../pages/hold/hold.service';
import { RequestService } from '../pages/request/request.service';
import { AuthService, UserData, SYSTEMCATEGORY } from './auth/auth.service';
import { IllSettingsService } from './ill-settings/ill-settings.service';
import { TARGETTYPE } from './search/search.service';
import { IListItem } from '../core/interfaces/list-interface';

export enum FULFILLMENT_TYPE {
  ILL_REQUEST,
  PLACE_HOLD,
  FULL_RECORD, // EContent or others handled through details page
}

/**
 * This service is an attempt to encapsulate all the ways that an item can be fulfilled,
 * with the goal of being agnostic to the type of request.
 */
@Injectable({
  providedIn: 'root',
})
export class FulfillmentService {
  private readonly _thirdParty = ['HP', 'OD', 'BX'];

  constructor(
    private _holdService: HoldService,
    private _requestService: RequestService,
    private _illSettingsService: IllSettingsService,
  ) {}

  /**
   * Returns the type of fulfillment to be made based on the System, Document and Holdings.
   * Verso CILL can be either a Place Hold or an ILL Request, based on whether or not the
   * document is owned by the library. Hoopla, Overdrive, 3rd party, etc are handled through the full record page
   * @param document
   * @param holdings
   * @returns FULFILLMENT_TYPE
   */
  getFulfillmentType(
    targetType: string,
    isEContent: boolean,
    userData: UserData,
  ): FULFILLMENT_TYPE {
    if (isEContent) {
      return userData.systemCategory === SYSTEMCATEGORY.SHAREIT
        ? FULFILLMENT_TYPE.ILL_REQUEST
        : FULFILLMENT_TYPE.FULL_RECORD;
    }

    if (
      (userData.systemCategory === SYSTEMCATEGORY.SHAREIT ||
        (userData.systemCategory === SYSTEMCATEGORY.VERSO && userData.illOptions.hasILL)) &&
      (targetType === TARGETTYPE.AG || targetType === TARGETTYPE.ZZ || targetType === TARGETTYPE.WM)
    ) {
      return FULFILLMENT_TYPE.ILL_REQUEST;
    }

    if (userData.systemCategory === SYSTEMCATEGORY.VERSO && targetType === TARGETTYPE.AV) {
      return FULFILLMENT_TYPE.PLACE_HOLD;
    }

    return FULFILLMENT_TYPE.FULL_RECORD;
  }

  getFulfillmentTypeByDocument(document: Document, userData: UserData): FULFILLMENT_TYPE {
    return this.getFulfillmentType(document.targetType, document.isEContent, userData);
  }

  /**
   * Get the label to display for the fulfillment button
   * @param fulfillmentType
   * @param userData (userData.illOptions?.hasILL)
   * @returns 'Request this Item' or 'Place Hold'. If VERSO and has ILL permissions, the label is 'Request this Item'
   */
  getFulfillmentLabel(fulfillmentType: FULFILLMENT_TYPE, userData: UserData): string {
    switch (fulfillmentType) {
      case FULFILLMENT_TYPE.ILL_REQUEST:
        return 'Place Request';
      case FULFILLMENT_TYPE.PLACE_HOLD:
        return userData.illOptions?.hasILL ? 'Place Request' : 'Place Hold';
      case FULFILLMENT_TYPE.FULL_RECORD:
        return 'View Details';
    }
  }

  /**
   * Checks if the document can be fulfilled based on the System, TargetType, ILL Options
   * Permission and, for ShareIt, whether the document has any requestable lenders
   * @param document
   * @param userData
   * @returns
   */
  showFulfillmentActionByDocument(document: Document, userData: UserData): boolean {
    return this.showFulfillmentAction(
      document.targetType,
      document.format,
      document.hasRequestableLenders,
      userData,
    );
  }

  showFulfillmentAction(
    targetType: string,
    format: string,
    hasRequestableLenders: boolean,
    userData: UserData,
  ): boolean {
    let show = false;
    // Hoopla, Overdrive and eContent are handled through the full record page
    if (this._thirdParty.includes(targetType)) {
      show = true;
    } else if (userData.systemCategory === SYSTEMCATEGORY.VERSO) {
      const allowReserve = this._holdService.allowPatronGuestReserve();
      if (allowReserve) {
        // Outside resources (AG/ZZ/WM) honour the participant record's hide setting; the local catalog (AV) never does
        const allowIllRequest =
          this._requestService.hasUserIllPermissions() &&
          (hasRequestableLenders || !userData.illOptions.hideRequestThisForNonRequestable);
        show =
          targetType === TARGETTYPE.AV ||
          ((targetType === TARGETTYPE.AG ||
            targetType === TARGETTYPE.ZZ ||
            targetType === TARGETTYPE.WM) &&
            allowIllRequest);
      }
    } else if (userData.systemCategory === SYSTEMCATEGORY.SHAREIT) {
      const showFormat = this._illSettingsService?.showFormat(format);
      if (
        userData.illOptions.showRequestThis &&
        showFormat &&
        (hasRequestableLenders || !userData.illOptions.hideRequestThisForNonRequestable)
      ) {
        show =
          targetType === TARGETTYPE.AV ||
          targetType === TARGETTYPE.AG ||
          targetType === TARGETTYPE.ZZ ||
          targetType === TARGETTYPE.WM;
      }
    }
    return show;
  }
}

/**
 * Returns true if any of the documents and user are eligible to fulfill the item.
 * true if Verso; user 'canPlaceHold' and
 *    AV
 *    or AG, ZZ, or WM, and the user has CILL permissions and
 *       the document has requestable lenders (unless illOptions.hideRequestThisForNonRequestable is off)
 * Else if ShareIt; illOptions.showRequestThis is true and
 *    the document format is in the illSettingsService.borrowerFormats and
 *    the document has requestable lenders
 *
 */
@Pipe({
  name: 'showFulfillmentAction',
  standalone: false,
})
export class ShowFulfillmentActionPipe implements PipeTransform {
  constructor(
    private _authService: AuthService,
    private _fulfillmentService: FulfillmentService,
  ) {}
  transform(value: any, type: 'document' | 'format' | 'result' | 'listItem'): boolean {
    if (!value) {
      return;
    }
    const userData = this._authService.getUserDataFromSession();
    let docs: Document[] = [];

    if (value instanceof Array) {
      if (type === 'format') {
        docs = (value as DocFormat[]).reduce((acc: Document[], curr: DocFormat) => {
          return [...acc, ...curr.documents];
        }, []);
      } else if (type === 'document') {
        docs = value as Document[];
      }
    } else {
      if (type === 'format') {
        docs = (value as DocFormat).documents;
      } else if (type === 'document') {
        docs.push(value as Document);
      } else if (type === 'result') {
        docs = (value as Result).formats.reduce((acc: Document[], curr: DocFormat) => {
          return [...acc, ...curr.documents];
        }, []);
      } else if (type === 'listItem') {
        // List items carry no lender information, so the requestable-lender check does not
        // apply to them and must not suppress the action.
        return this._fulfillmentService.showFulfillmentAction(
          (value as IListItem).databaseType,
          (value as IListItem).format.short,
          true,
          userData,
        );
      }
    }

    if (docs.length) {
      for (const document of docs) {
        if (this._fulfillmentService.showFulfillmentActionByDocument(document, userData)) {
          return true;
        }
      }
    }
    return false;
  }
}

@Pipe({
  name: 'fulfillmentType',
  standalone: false,
})
export class FulfillmentTypePipe implements PipeTransform {
  constructor(
    private _fulfillmentService: FulfillmentService,
    private _authService: AuthService,
  ) {}
  transform(value: any, type: 'document' | 'listItem' = 'document'): FULFILLMENT_TYPE {
    if (!document) {
      return;
    }
    const userData = this._authService.getUserDataFromSession();

    if (type === 'listItem') {
      return this._fulfillmentService.getFulfillmentType(
        (value as IListItem).databaseType,
        false,
        userData,
      );
    } else if (type === 'document') {
      return this._fulfillmentService.getFulfillmentTypeByDocument(value, userData);
    } else {
      return FULFILLMENT_TYPE.FULL_RECORD;
    }
  }
}

@Pipe({
  name: 'fulfillmentLabel',
  standalone: false,
})
export class FulfillmentLabelPipe implements PipeTransform {
  constructor(
    private _fulfillmentService: FulfillmentService,
    private _authService: AuthService,
  ) {}
  transform(fulfillmentType: FULFILLMENT_TYPE): string {
    if (fulfillmentType == null) {
      return;
    }
    const userData = this._authService.getUserDataFromSession();
    return this._fulfillmentService.getFulfillmentLabel(fulfillmentType, userData);
  }
}
